import React, { useState } from 'react'
import { FiX, FiCheck, FiLoader } from 'react-icons/fi'
import api from '../config/axios'
import { useAuthStore } from '../store/authStore'
import { useSearchStore } from '../store/searchStore'

function ContactModal({ onClose }) {
  const { user } = useAuthStore()
  const { results, searchedDimensions } = useSearchStore()
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState('idle') // idle | loading | done | error

  const fmt = v => v != null ? parseFloat(parseFloat(v).toFixed(2)) : '—'

  const bestFit = results.length > 0
    ? results.reduce((best, r) =>
        r.volumeRatio != null && (best === null || r.volumeRatio < best.volumeRatio) ? r : best
      , null)
    : null

  const handleSend = async () => {
    setStatus('loading')
    try {
      await api.post('/api/auth/contact-enquiry', {
        user: {
          uid: user?.uid,
          displayName: user?.displayName,
          companyName: user?.companyName,
          email: user?.email,
          cellNumber: user?.cellNumber,
        },
        searchedDimensions: searchedDimensions || null,
        bestFit: bestFit ? {
          model: bestFit.model,
          series: bestFit.series,
          length: bestFit.length,
          width: bestFit.width,
          depth: bestFit.depth,
        } : null,
        message: message.trim() || null,
      })
      setStatus('done')
    } catch (err) {
      console.error('Contact enquiry failed:', err)
      setStatus('error')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70" onClick={status === 'loading' ? undefined : onClose} />

      <div className="relative bg-bg-card border border-border-dark rounded-xl shadow-2xl w-full max-w-md p-6 space-y-5">

        {status !== 'loading' && (
          <button onClick={onClose} className="absolute top-4 right-4 text-gray-500 hover:text-white transition">
            <FiX size={20} />
          </button>
        )}

        {status === 'done' ? (
          <div className="text-center py-4 space-y-3">
            <div className="w-14 h-14 rounded-full bg-accent-teal/20 border border-accent-teal flex items-center justify-center mx-auto">
              <FiCheck size={28} className="text-accent-teal" />
            </div>
            <h3 className="text-xl font-bold text-white">Message Sent</h3>
            <p className="text-gray-400 text-sm">Our team will be in touch with you.</p>
            <button onClick={onClose} className="w-full btn-primary mt-2">Close</button>
          </div>
        ) : (
          <>
            <div>
              <h3 className="text-xl font-bold text-accent-yellow">Get in Touch</h3>
              <p className="text-gray-400 text-sm mt-1">
                We'll receive your details and any message you add below.
              </p>
            </div>

            {/* User details */}
            <div className="bg-bg-dark rounded-lg p-4 space-y-2 text-sm">
              <p className="text-xs font-semibold text-accent-teal uppercase tracking-widest mb-2">Your Details</p>
              <div className="flex justify-between">
                <span className="text-gray-400">Name</span>
                <span className="text-white">{user?.displayName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Company</span>
                <span className="text-white">{user?.companyName || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Email</span>
                <span className="text-white">{user?.email || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-400">Cell</span>
                <span className="text-white">{user?.cellNumber || '—'}</span>
              </div>
            </div>

            {/* Search context — only if available */}
            {(searchedDimensions || bestFit) && (
              <div className="bg-bg-dark rounded-lg p-4 space-y-2 text-sm">
                <p className="text-xs font-semibold text-accent-teal uppercase tracking-widest mb-2">Search Context</p>
                {searchedDimensions && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Searched</span>
                    <span className="text-white">
                      L {fmt(searchedDimensions.length)} × W {fmt(searchedDimensions.width)} × D {fmt(searchedDimensions.depth)} cm
                    </span>
                  </div>
                )}
                {bestFit && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Best fit</span>
                    <span className="text-accent-yellow font-semibold">{bestFit.model}</span>
                  </div>
                )}
              </div>
            )}

            {/* Optional message */}
            <div>
              <label className="block text-xs font-semibold text-accent-teal uppercase tracking-widest mb-2">
                Message <span className="text-gray-500 normal-case font-normal">(optional)</span>
              </label>
              <textarea
                value={message}
                onChange={e => setMessage(e.target.value)}
                placeholder="Anything you'd like to add…"
                rows={3}
                disabled={status === 'loading'}
                className="w-full px-3 py-2 bg-bg-dark border border-border-dark rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow text-sm resize-none disabled:opacity-50"
              />
            </div>

            {status === 'error' && (
              <p className="text-red-400 text-sm text-center">
                Something went wrong. Please try again or contact us directly.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={onClose}
                disabled={status === 'loading'}
                className="flex-1 px-4 py-2 border border-border-dark text-gray-400 hover:text-white hover:border-gray-400 rounded transition text-sm disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSend}
                disabled={status === 'loading'}
                className="flex-1 btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
              >
                {status === 'loading' ? (
                  <><FiLoader size={15} className="animate-spin" /> Sending…</>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default ContactModal
